import { useState } from 'react';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';
import Input from '../../../components/ui/Input';
import Select from '../../../components/ui/Select';
import { Checkbox } from '../../../components/ui/Checkbox';

const UserProfile = ({ user, onUpdate, onDelete }) => {
  const [isEditing, setIsEditing] = useState(false);
  const [formData, setFormData] = useState({
    name: user?.name,
    email: user?.email,
    role: user?.role,
    department: user?.department,
    status: user?.status,
    mfaEnabled: user?.mfaEnabled,
  });

  const roleOptions = [
    { value: 'Admin', label: 'Admin - Full System Access' },
    { value: 'Ops', label: 'Operations - Shipment Management' },
    { value: 'Finance', label: 'Finance - Cost & Fee Access' },
    { value: 'RNR', label: 'RNR - Limited Read Access' },
  ];

  const statusOptions = [
    { value: 'active', label: 'Active' },
    { value: 'inactive', label: 'Inactive' },
    { value: 'suspended', label: 'Suspended' },
  ];

  const departmentOptions = [
    { value: 'Operations', label: 'Operations' },
    { value: 'Finance', label: 'Finance' },
    { value: 'Administration', label: 'Administration' },
    { value: 'Trading', label: 'Trading' },
  ];

  const handleSave = () => {
    onUpdate(user?.id, formData);
    setIsEditing(false);
  };

  const handleCancel = () => {
    setFormData({
      name: user?.name,
      email: user?.email,
      role: user?.role,
      department: user?.department,
      status: user?.status,
      mfaEnabled: user?.mfaEnabled,
    });
    setIsEditing(false);
  };

  const formatDate = (date) => {
    return new Date(date)?.toLocaleDateString('en-GB', {
      day: '2-digit',
      month: 'short',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  };

  return (
    <div className="bg-card rounded-lg border border-border">
      <div className="p-6 border-b border-border">
        <div className="flex items-start justify-between mb-4">
          <div className="flex items-center gap-4">
            <div className="w-16 h-16 rounded-full bg-primary/10 flex items-center justify-center">
              <span className="text-2xl font-semibold text-primary">
                {user?.name?.split(' ')?.map((n) => n?.[0])?.join('')?.toUpperCase()}
              </span>
            </div>
            <div>
              <h2 className="text-xl font-semibold text-foreground">
                {user?.name}
              </h2>
              <p className="text-sm text-muted-foreground">{user?.email}</p>
              <div className="flex items-center gap-2 mt-2">
                <span className="px-2 py-1 rounded text-xs font-medium bg-primary/10 text-primary">
                  {user?.role}
                </span>
                {user?.mfaEnabled && (
                  <span className="flex items-center gap-1 text-xs text-success">
                    <Icon name="Shield" size={12} />
                    MFA Enabled
                  </span>
                )}
              </div>
            </div>
          </div>
          <div className="flex items-center gap-2">
            {!isEditing ? (
              <>
                <Button
                  variant="outline"
                  size="sm"
                  iconName="Edit"
                  onClick={() => setIsEditing(true)}
                >
                  Edit
                </Button>
                <Button
                  variant="destructive"
                  size="sm"
                  iconName="Trash2"
                  onClick={() => onDelete(user?.id)}
                >
                  Delete
                </Button>
              </>
            ) : (
              <>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={handleCancel}
                >
                  Cancel
                </Button>
                <Button
                  variant="default"
                  size="sm"
                  iconName="Save"
                  onClick={handleSave}
                >
                  Save
                </Button>
              </>
            )}
          </div>
        </div>
      </div>
      <div className="p-6 space-y-6">
        <div className="grid grid-cols-2 gap-4">
          <Input
            label="Full Name"
            type="text"
            value={formData?.name}
            onChange={(e) =>
              setFormData({ ...formData, name: e?.target?.value })
            }
            disabled={!isEditing}
            required
          />
          <Input
            label="Email Address"
            type="email"
            value={formData?.email}
            onChange={(e) =>
              setFormData({ ...formData, email: e?.target?.value })
            }
            disabled={!isEditing}
            required
          />
        </div>

        <div className="grid grid-cols-3 gap-4">
          <Select
            label="Role"
            options={roleOptions}
            value={formData?.role}
            onChange={(value) => setFormData({ ...formData, role: value })}
            disabled={!isEditing}
            required
          />
          <Select
            label="Department"
            options={departmentOptions}
            value={formData?.department}
            onChange={(value) =>
              setFormData({ ...formData, department: value })
            }
            disabled={!isEditing}
          />
          <Select
            label="Status"
            options={statusOptions}
            value={formData?.status}
            onChange={(value) => setFormData({ ...formData, status: value })}
            disabled={!isEditing}
            required
          />
        </div>

        <div className="pt-4 border-t border-border">
          <Checkbox
            label="Enable Multi-Factor Authentication (MFA)"
            description="Require additional verification for login"
            checked={formData?.mfaEnabled}
            onChange={(e) =>
              setFormData({ ...formData, mfaEnabled: e?.target?.checked })
            }
            disabled={!isEditing}
          />
        </div>

        <div className="pt-4 border-t border-border">
          <h3 className="text-sm font-semibold text-foreground mb-3">
            Account Information
          </h3>
          <div className="grid grid-cols-2 gap-4 text-sm">
            <div>
              <span className="text-muted-foreground">User ID:</span>
              <span className="ml-2 text-foreground font-medium">
                {user?.id}
              </span>
            </div>
            <div>
              <span className="text-muted-foreground">Created:</span>
              <span className="ml-2 text-foreground">
                {formatDate(user?.createdAt)}
              </span>
            </div>
            <div>
              <span className="text-muted-foreground">Last Login:</span>
              <span className="ml-2 text-foreground">
                {formatDate(user?.lastLogin)}
              </span>
            </div>
            <div>
              <span className="text-muted-foreground">Login Count:</span>
              <span className="ml-2 text-foreground">{user?.loginCount}</span>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default UserProfile;